/**
 * useToast Composable
 *
 * Global toast notification system. Provides a reactive array of toasts
 * that any component can push to. The BaseToast component renders them.
 */

import { ref } from 'vue'

export interface Toast {
  id: string
  message: string
  variant: 'success' | 'error' | 'warning' | 'info'
  duration: number
}

// Shared reactive state (singleton)
const toasts = ref<Toast[]>([])

let counter = 0

export function useToast() {
  function toast(message: string, variant: Toast['variant'] = 'info', duration = 4000) {
    const id = `toast-${++counter}-${Date.now()}`
    const t: Toast = { id, message, variant, duration }
    toasts.value = [...toasts.value, t]

    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => {
        toasts.value = toasts.value.filter(to => to.id !== id)
      }, duration)
    }

    return id
  }

  function dismiss(id: string) {
    toasts.value = toasts.value.filter(t => t.id !== id)
  }

  function success(message: string, duration?: number) {
    return toast(message, 'success', duration)
  }

  function error(message: string, duration?: number) {
    return toast(message, 'error', duration)
  }

  function warning(message: string, duration?: number) {
    return toast(message, 'warning', duration)
  }

  function info(message: string, duration?: number) {
    return toast(message, 'info', duration)
  }

  return {
    toasts,  // reactive — consumed by BaseToast component
    toast,
    success,
    error,
    warning,
    info,
    dismiss
  }
}
